import React, { useEffect, useRef, useState } from 'react';
import useClawWsStore from '@src/stores/useClawWsStore';
import type { ClawChatMessage, ContentBlock, MessageContent, ToolCall, ThinkingBlock, LifecycleEvent } from '@src/stores/useClawWsStore';
import { useClawWs, isFreshSession } from '@src/hooks/useClawWs';
import Md from '@src/components/common/md-render/ClawMd';
import './MessageList.less';
import { CodeviewLineIcon, ShouqiLineIcon, SkillLineIcon, StarLineIcon, ZhankaiLineIcon } from '@kim/icon';
import { EndLineIcon, FileFoldIcon } from '@kim/ui';
import loading from '@src/assets/images/agent_running.png';

// ─── 历史消息响应类型 ──────────────────────────────────────────────────────────

/** content 块中工具调用块的结构（assistant 消息） */
interface ToolCallBlock {
  type: 'toolCall';
  id: string;
  name: string;
  arguments?: Record<string, unknown>;
  [key: string]: unknown;
}

interface HistoryMessage {
  /** 实际数据中还可能出现 toolResult 等非展示角色，统一用 string 接收 */
  role: string;
  content: MessageContent | ToolCallBlock[];
  timestamp?: number;
  idempotencyKey?: string;
  /** 服务端可能不返回 idempotencyKey，改用 runId 作为唯一标识 */
  runId?: string;
  errorMessage?: string;
  /** toolResult 消息字段 */
  toolCallId?: string;
  toolName?: string;
  isError?: boolean;
}

/**
 * 从原始历史消息序列里按轮次提取工具调用并关联到对应的 assistant 文本消息。
 *
 * 策略：
 * - 遇到 user 消息：开启新一轮，重置待处理工具调用列表
 * - 遇到 assistant/toolCall 消息：提取工具调用，加入待处理列表
 * - 遇到 toolResult 消息：更新对应工具调用的结果
 * - 遇到 assistant/text 消息：将待处理工具调用与该消息关联，写入展示列表，重置列表
 * - 循环结束后仍有待处理工具调用（如 aborted）：作为孤立工具调用返回
 */
function processHistory(
  raw: HistoryMessage[],
  sessionKey: string,
): {
  displayMsgs: ClawChatMessage[];
  /** 以 idempotencyKey / timestamp 字符串为 key，映射该轮对话 assistant 的工具调用 */
  toolCallsByKey: Map<string, ToolCall[]>;
  /** 无对应 text 响应的孤立工具调用（如 aborted 的会话） */
  orphanedToolCalls: ToolCall[];
  /** 孤立 thinking 块（与孤立工具调用配套） */
  orphanedThinking: ThinkingBlock[];
} {
  const toolCallMap = new Map<string, ToolCall>();
  let pendingIds: string[] = [];
  /** 当前轮次从中间 assistant 消息里累积的 thinking 块 */
  let pendingThinking: ThinkingBlock[] = [];
  const toolCallsByKey = new Map<string, ToolCall[]>();
  const displayMsgs: ClawChatMessage[] = [];

  const mkToolResult = (msg: HistoryMessage): string =>
    Array.isArray(msg.content)
      ? (msg.content as Array<{ type: string; text?: string }>)
          .filter((b) => b.type === 'text')
          .map((b) => b.text ?? '')
          .join('\n')
      : String(msg.content ?? '');

  const hasText = (msg: HistoryMessage): boolean => {
    if (Array.isArray(msg.content)) {
      return (msg.content as Array<{ type: string }>).some((b) => b.type === 'text');
    }
    return typeof msg.content === 'string' && (msg.content as string).trim() !== '';
  };

  const flush = (msg: HistoryMessage) => {
    const toolCallsForMsg = pendingIds
      .map((id) => toolCallMap.get(id))
      .filter((tc): tc is ToolCall => !!tc);

    // 将本轮从中间消息累积的 thinking 块合并到最终消息 content 的头部
    // 若最终消息自身已有 thinking 块，则不重复添加
    let mergedContent = msg.content as MessageContent;
    if (pendingThinking.length > 0 && Array.isArray(mergedContent)) {
      const existingThinkingTexts = new Set(
        (mergedContent as ContentBlock[])
          .filter((b): b is ThinkingBlock => b.type === 'thinking')
          .map((b) => b.thinking),
      );
      const newBlocks = pendingThinking.filter((b) => !existingThinkingTexts.has(b.thinking));
      if (newBlocks.length > 0) {
        mergedContent = [...newBlocks, ...(mergedContent as ContentBlock[])] as ContentBlock[];
      }
    }

    const displayMsg: ClawChatMessage = {
      role: msg.role as ClawChatMessage['role'],
      sessionKey,
      content: mergedContent,
      done: true,
      timestamp: msg.timestamp,
      idempotencyKey: msg.idempotencyKey ?? msg.runId,
    };
    displayMsgs.push(displayMsg);

    // 用多个稳定 key 都能查到该轮的工具调用
    const keys: string[] = [];
    if (msg.idempotencyKey) keys.push(msg.idempotencyKey);
    if (msg.runId && msg.runId !== msg.idempotencyKey) keys.push(msg.runId);
    if (msg.timestamp) keys.push(String(msg.timestamp));
    for (const k of keys) toolCallsByKey.set(k, toolCallsForMsg);

    pendingIds = [];
    pendingThinking = [];
  };

  for (const msg of raw) {
    if (msg.errorMessage) continue;

    if (msg.role === 'user') {
      pendingIds = [];
      pendingThinking = [];
      if (hasText(msg)) {
        displayMsgs.push({
          role: 'user',
          sessionKey,
          content: msg.content as MessageContent,
          done: true,
          timestamp: msg.timestamp,
          idempotencyKey: msg.idempotencyKey ?? msg.runId,
        });
      }
    } else if (msg.role === 'assistant') {
      const blocks = Array.isArray(msg.content)
        ? (msg.content as Array<{ type: string; [k: string]: unknown }>)
        : [];
      const toolCallBlocks = blocks.filter((b) => b.type === 'toolCall') as ToolCallBlock[];

      // 收集 thinking 块（中间消息里的思考内容，flush 时合并到最终消息）
      // 附上所在消息的时间戳，用于与 tool call 混合排序
      for (const block of blocks) {
        if (block.type === 'thinking' && typeof (block as { thinking?: unknown }).thinking === 'string') {
          pendingThinking.push({ ...(block as unknown as ThinkingBlock), at: msg.timestamp ?? 0 });
        }
      }

      // 提取工具调用块
      for (const block of toolCallBlocks) {
        if (!toolCallMap.has(block.id)) {
          toolCallMap.set(block.id, {
            toolCallId: block.id,
            name: block.name,
            sessionKey,
            runId: msg.idempotencyKey ?? msg.runId ?? '',
            args: block.arguments,
            status: 'done',
            startedAt: msg.timestamp ?? 0,
          });
        }
        if (!pendingIds.includes(block.id)) pendingIds.push(block.id);
      }

      // 如果同时含有 text 内容，则作为最终响应 flush
      if (hasText(msg)) flush(msg);
    } else if (msg.role === 'toolResult' && msg.toolCallId) {
      const existing = toolCallMap.get(msg.toolCallId);
      if (existing) {
        toolCallMap.set(msg.toolCallId, {
          ...existing,
          status: msg.isError ? 'error' : 'done',
          isError: msg.isError,
          result: mkToolResult(msg),
          finishedAt: msg.timestamp,
        });
      }
    }
  }

  const orphanedToolCalls = pendingIds
    .map((id) => toolCallMap.get(id))
    .filter((tc): tc is ToolCall => !!tc)
    .sort((a, b) => a.startedAt - b.startedAt);

  return { displayMsgs, toolCallsByKey, orphanedToolCalls, orphanedThinking: pendingThinking };
}

// ─── 工具：提取纯文本 ─────────────────────────────────────────────────────────

export function getTextContent(content: MessageContent): string {
  if (typeof content === 'string') return content;
  return content
    .filter((b): b is Extract<ContentBlock, { type: 'text' }> => b.type === 'text')
    .map((b) => b.text)
    .join('\n');
}

// ─── 思考面板 ─────────────────────────────────────────────────────────────────

/** 根据工具名返回可读描述前缀 */
function getToolLabel(name: string, args?: Record<string, unknown>): string {
  const lower = name.toLowerCase();
  if (lower === 'skill' || lower.includes('skill') || (['read', 'view', 'cat'].includes(lower)  && JSON.stringify(args).includes('SKILL.md'))) return '查看了技能';
  if (['read', 'view', 'cat'].includes(lower)) return '查看了文件';
  if (['write', 'create', 'edit', 'patch'].includes(lower)) return '编辑了文件';
  if (['exec', 'bash', 'shell', 'run', 'python'].includes(lower)) return '执行了命令';
  if (['search', 'grep', 'find'].includes(lower)) return '搜索了内容';
  if (['glob', 'ls', 'list'].includes(lower)) return '列举了目录';
  return `调用了 ${name}`;
}

/** 从 args 提取最具代表性的单行摘要 */
function getArgSummary(args?: Record<string, unknown>): string {
  if (!args) return '';
  const keys = ['path', 'file_path', 'command', 'script', 'query', 'file', 'name', 'pattern'];
  for (const k of keys) {
    if (typeof args[k] === 'string' && (args[k] as string).trim()) {
      return args[k] as string;
    }
  }
  // fallback: first string value
  for (const v of Object.values(args)) {
    if (typeof v === 'string' && v.trim()) return v;
  }
  return '';
}

/** 根据工具名选择图标字符 */
function getToolIcon(name: string, args?: Record<string, unknown>) {
  const lower = name.toLowerCase();
  if (lower === 'skill' || lower.includes('skill') || (['read', 'view', 'cat'].includes(lower)  && JSON.stringify(args).includes('SKILL.md'))) return <SkillLineIcon />;
  if (['read', 'view', 'cat', 'glob', 'ls', 'list', 'grep', 'find', 'search'].includes(lower)) return <CodeviewLineIcon />;
  if (['write', 'create', 'edit', 'patch'].includes(lower)) return <FileFoldIcon />;
  if (['exec', 'bash', 'shell', 'run', 'python'].includes(lower)) return <CodeviewLineIcon />;
  return <CodeviewLineIcon />;
}

interface ThinkingPanelProps {
  toolCalls: ToolCall[];
  thinkingBlocks: ThinkingBlock[];
  lifecycleEvents: LifecycleEvent[];
  isStreaming: boolean;
}

/** 统一时间线条目，用于混合排序工具调用、lifecycle 事件与 thinking 块 */
type TimelineItem =
  | { kind: 'tool'; call: ToolCall; at: number }
  | { kind: 'lifecycle'; event: LifecycleEvent; at: number }
  | { kind: 'thinking'; blocks: ThinkingBlock[]; at: number }
  | { kind: 'done'; at: number };

const ThinkingPanel: React.FC<ThinkingPanelProps> = ({ toolCalls, thinkingBlocks, isStreaming, lifecycleEvents }) => {
  const [expanded, setExpanded] = useState(false);

  const hasToolCalls = toolCalls.length > 0;
  const hasThinking = thinkingBlocks.length > 0;
  const hasLifecycle = lifecycleEvents.length > 0;
  /** 有实质内容可展开（排除纯等待状态） */
  const hasContent = hasToolCalls || hasThinking || hasLifecycle;

  // 只有在流式中、有工具调用、或有思考块时才渲染
  if (!isStreaming && !hasContent) return null;

  const title = isStreaming ? '正在思考...' : '已思考完成';

  // 把所有 thinking 块合并为一条时间线条目（取最小 at 作为排序基准）
  // at 为 0 时（无时间戳）视为最早，排在最前面（thinking 通常早于 tool call）
  const thinkingAt = thinkingBlocks.length > 0
    ? Math.min(...thinkingBlocks.map((b) => b.at ?? 0))
    : 0;

  const timeline: TimelineItem[] = [
    ...lifecycleEvents.map<TimelineItem>((event) => ({ kind: 'lifecycle', event, at: event.startedAt })),
    ...toolCalls.map<TimelineItem>((call) => ({ kind: 'tool', call, at: call.startedAt })),
    ...(thinkingBlocks.length > 0
      ? [{ kind: 'thinking' as const, blocks: thinkingBlocks, at: thinkingAt }]
      : []),
  ].sort((a, b) => a.at - b.at);

  // 历史消息没有 lifecycle 事件时，在头部补一个合成的开始条目
  if (!hasLifecycle && hasContent) {
    timeline.unshift({
      kind: 'lifecycle',
      event: { runId: '__synthetic_start__', sessionKey: '', phase: 'start', startedAt: -Infinity },
      at: -Infinity,
    });
  }

  // 非流式（已完成）时在末尾追加"完成"条目
  if (!isStreaming && hasContent) {
    timeline.push({ kind: 'done', at: Infinity });
  }

  return (
    <div className={`msg-thinking${expanded ? ' msg-thinking--expanded' : ''}`}>
      <div
        className="msg-thinking__header"
        onClick={() => hasContent && setExpanded((v) => !v)}
        style={hasContent ? undefined : { cursor: 'default' }}
      >
        <span className={`msg-thinking__title${isStreaming ? ' msg-thinking__title--streaming' : ''}`}>
          {isStreaming && <img src={loading} alt="loading" className="msg-thinking__loading" />}
          {title}
        </span>
        <button
          className="msg-thinking__toggle"
          type="button"
          aria-label={expanded ? '收起' : '展开'}
          style={hasContent ? undefined : { visibility: 'hidden' }}
        >
          {!expanded ? <ZhankaiLineIcon /> : <ShouqiLineIcon />}
        </button>
      </div>

      {expanded && hasContent && (
        <div className="msg-thinking__body">
          {timeline.map((item, idx) => {
            // lifecycle start 条目
            if (item.kind === 'lifecycle') {
              return (
                <div key={`lc-${item.event.runId}-${idx}`} className="msg-thinking__item msg-thinking__item--done">
                  <div className="msg-thinking__item-row">
                    <span className="msg-thinking__item-icon">
                       <EndLineIcon />
                    </span>
                    <span className="msg-thinking__item-text">收到，准备开工...</span>
                  </div>
                </div>
              );
            }

            // 完成条目（非流式时追加在末尾）
            if (item.kind === 'done') {
              return (
                <div key="timeline-done" className="msg-thinking__item msg-thinking__item--done">
                  <div className="msg-thinking__item-row">
                    <span className="msg-thinking__item-icon">
                       <EndLineIcon />
                    </span>
                    <span className="msg-thinking__item-text">完成了，马上呈现</span>
                  </div>
                </div>
              );
            }

            if (item.kind === 'tool') {
              const { call } = item;
              const label = getToolLabel(call.name, call.args);
              const argSummary = getArgSummary(call.args);
              const icon = getToolIcon(call.name, call.args);
              return (
                <div key={call.toolCallId} className={`msg-thinking__item msg-thinking__item--${call.status}`}>
                  <div className="msg-thinking__item-row">
                    <span className="msg-thinking__item-icon">{icon}</span>
                    <span
                      className="msg-thinking__item-text"
                      title={argSummary ? `${label}：${argSummary}` : label}
                    >
                      {label}
                      {argSummary ? `：${argSummary}` : ''}
                    </span>
                  </div>
                  {argSummary && (
                    <div className="msg-thinking__item-sub">
                      <span className="msg-thinking__item-chip">{argSummary}</span>
                    </div>
                  )}
                </div>
              );
            }

            // kind === 'thinking'
            const thinkingText = item.blocks.map((b) => b.thinking).join('\n\n');
            return (
              <div key="thinking-block" className="msg-thinking__item msg-thinking__item--thinking">
                <div className="msg-thinking__item-row">
                  <span className="msg-thinking__item-icon"><StarLineIcon /></span>
                  <span className="msg-thinking__item-text">模型思考过程</span>
                </div>
                <div className="msg-thinking__item-sub msg-thinking__item-sub--thinking">
                  <div className="msg-thinking__thinking-text">{thinkingText}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── 消息内容渲染 ─────────────────────────────────────────────────────────────

interface ContentRendererProps {
  content: MessageContent;
  isStreaming: boolean;
}

const formatTimestamp = (ts: number) =>
  new Date(ts).toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' }) +
  ' ' +
  new Date(ts).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });

const ContentRenderer: React.FC<ContentRendererProps> = ({ content, isStreaming }) => {
  const text = getTextContent(content);
  if (!isStreaming && text.trim().replace(/\n/g, '') === '') {
    return null;
  }

  return (
    <>
      {text ? (
        <div className="msg-bubble__content-wrapper">
          <Md source={text} withFilter={false} />
        </div>
      ) : null}
    </>
  );
};

// ─── 消息气泡 ──────────────────────────────────────────────────────────────────

interface BubbleProps {
  msg: ClawChatMessage;
  /** 仅流式 assistant 消息传入当前 run 的工具调用列表 */
  toolCalls?: ToolCall[];
  /** 对应 run 的 lifecycle start 事件 */
  lifecycleEvents?: LifecycleEvent[];
}

/** 是否与 ContentRenderer 一致地认为有正文（忽略仅空白/换行） */
function hasRenderableTextBody(msg: ClawChatMessage): boolean {
  return getTextContent(msg.content).trim().replace(/\n/g, '') !== '';
}

/** 无任何可展示内容时不渲染气泡（避免仅时间戳或空容器） */
function shouldShowBubble(msg: ClawChatMessage): boolean {
  if (msg.role === 'user') return hasRenderableTextBody(msg);
  const isStreaming = !msg.done && msg.role === 'assistant';
  if (isStreaming) return true;
  return hasRenderableTextBody(msg);
}

const Bubble: React.FC<BubbleProps> = ({ msg, toolCalls, lifecycleEvents: lcs }) => {
  if (!shouldShowBubble(msg)) return null;

  const isUser = msg.role === 'user';
  const isStreaming = !msg.done && msg.role === 'assistant';
  const textContent = getTextContent(msg.content);
  /** 与 ContentRenderer 一致：无正文时不展示思考条（流式中仍展示，直到正文开始） */
  const hasTextBody = hasRenderableTextBody(msg);

  // 从消息 content 里提取 ThinkingBlock（历史消息里的模型思考内容）
  const thinkingBlocks = Array.isArray(msg.content)
    ? msg.content.filter((b): b is ThinkingBlock => b.type === 'thinking')
    : [];

  return (
    <div className={`msg-bubble msg-bubble--${msg.role}`}>
      {isUser ? (
        <div className="msg-bubble__content msg-bubble__content--user">
          <Md source={textContent} />
        </div>
      ) : (
        <div className="msg-bubble__content msg-bubble__content--assistant">
          {msg.timestamp ? (
            <div className="msg-bubble__content-time">
              {formatTimestamp(msg.timestamp)}
            </div>
          ) : null}
          {(isStreaming || hasTextBody) && (
            <ThinkingPanel
              toolCalls={toolCalls ?? []}
              thinkingBlocks={thinkingBlocks}
              lifecycleEvents={lcs ?? []}
              isStreaming={isStreaming}
            />
          )}
          <ContentRenderer content={msg.content} isStreaming={isStreaming} />
        </div>
      )}
    </div>
  );
};

// ─── MessageList ──────────────────────────────────────────────────────────────

/** 服务端会对客户端 UUID 加前缀（如 agent:main:<uuid>），取末段作为归一化 key */
const getBaseKey = (key: string) => key.split(':').pop() ?? key;

/** 距底部多少 px 以内视为「在底部」，触发自动跟随滚动 */
const NEAR_BOTTOM_THRESHOLD = 80;

/**
 * 列表行稳定 ID（与 React key 一致）。
 * 必须用 role+下标：流式占位在 finishMessage 后会带上 idempotencyKey，若用 key 参与 id，
 * 选中态会与父组件 allDisplayedMsgs 里的 getMsgId 不一致，导致分享提交缺消息。
 */
export const getMsgId = (msg: ClawChatMessage, idx: number): string =>
  `${msg.role}-${idx}`;

const SelectRadio: React.FC<{ checked: boolean }> = ({ checked }) => (
  <span className={`msg-select-radio${checked ? ' msg-select-radio--checked' : ''}`}>
    {checked && (
      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
        <path d="M1 3.5L3.8 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )}
  </span>
);

interface MessageListProps {
  shareMode?: boolean;
  selectedIds?: Set<string>;
  onToggle?: (id: string) => void;
  onIdsChange?: (ids: string[]) => void;
  onMessagesChange?: (msgs: ClawChatMessage[]) => void;
}

export const MessageList: React.FC<MessageListProps> = ({
  shareMode,
  selectedIds,
  onToggle,
  onIdsChange,
  onMessagesChange,
}) => {
  const { currentSessionKey, messages, toolCalls, lifecycleEvents } = useClawWsStore();
  const { fetchHistory } = useClawWs();
  const [historyMsgs, setHistoryMsgs] = useState<ClawChatMessage[]>([]);
  /** 历史工具调用，按 idempotencyKey / timestamp 映射到对应 assistant 轮次 */
  const [historyToolCallsByKey, setHistoryToolCallsByKey] = useState<Map<string, ToolCall[]>>(new Map());
  /** 无对应 text 响应的孤立工具调用（aborted 会话等） */
  const [orphanedHistoryToolCalls, setOrphanedHistoryToolCalls] = useState<ToolCall[]>([]);
  /** 孤立 thinking 块（与孤立工具调用配套，aborted 会话等） */
  const [orphanedHistoryThinking, setOrphanedHistoryThinking] = useState<ThinkingBlock[]>([]);
  const [loading, setLoading] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  /** 记录上一次真正发起 fetch 的归一化 key，防止 key 前缀变化触发重复请求 */
  const lastFetchedBaseKey = useRef<string | null>(null);
  /** 当前 session 是否已完成首次滚动到底部 */
  const initialScrolledRef = useRef(false);
  /** 用户是否处于「接近底部」状态；新消息到来时只有此状态才自动跟随 */
  const isNearBottomRef = useRef(true);

  // 监听滚动，实时判断是否在底部附近
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      isNearBottomRef.current = scrollHeight - scrollTop - clientHeight <= NEAR_BOTTOM_THRESHOLD;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // session 切换时拉取历史
  useEffect(() => {
    if (!currentSessionKey) {
      setHistoryMsgs([]);
      setHistoryToolCallsByKey(new Map());
      setOrphanedHistoryToolCalls([]);
      setOrphanedHistoryThinking([]);
      lastFetchedBaseKey.current = null;
      initialScrolledRef.current = false;
      isNearBottomRef.current = true;
      prevUserMsgCountRef.current = 0;
      return;
    }

    // 归一化：去掉 agent:main: 等前缀后比较，避免 remap 导致的重复 fetch
    const baseKey = getBaseKey(currentSessionKey);
    if (lastFetchedBaseKey.current === baseKey) {
      return;
    }
    lastFetchedBaseKey.current = baseKey;
    initialScrolledRef.current = false;
    isNearBottomRef.current = true;
    prevUserMsgCountRef.current = 0;

    // 新建 session 不需要拉历史：消息已在 store 中，拉空历史反而会误清数据
    if (isFreshSession(currentSessionKey)) return;

    let cancelled = false;
    setLoading(true);
    fetchHistory(currentSessionKey, 1000)
      .then((res) => {
        if (cancelled) return;
        const raw = (res as { messages?: HistoryMessage[] }).messages ?? [];
        const { displayMsgs, toolCallsByKey, orphanedToolCalls, orphanedThinking } = processHistory(raw, currentSessionKey);
        setHistoryMsgs(displayMsgs);
        setHistoryToolCallsByKey(toolCallsByKey);
        setOrphanedHistoryToolCalls(orphanedToolCalls);
        setOrphanedHistoryThinking(orphanedThinking);
      })
      .catch(() => {
        if (!cancelled) {
          setHistoryMsgs([]);
          setHistoryToolCallsByKey(new Map());
          setOrphanedHistoryToolCalls([]);
          setOrphanedHistoryThinking([]);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [currentSessionKey, fetchHistory]);

  // 合并历史 + 本次会话新消息，优先按 idempotencyKey 去重，
  // 历史消息无 idempotencyKey 时降级按 role+内容 去重，避免双份显示
  // sessionNewMsgs 先按 idempotencyKey 自身去重（store 可能因重连等原因写入重复条目）
  const rawSessionMsgs = messages.filter((m) => m.sessionKey === currentSessionKey);
  const seenNewKeys = new Set<string>();
  const sessionNewMsgs = rawSessionMsgs.filter((m) => {
    if (!m.idempotencyKey) return true;
    if (seenNewKeys.has(m.idempotencyKey)) return false;
    seenNewKeys.add(m.idempotencyKey);
    return true;
  });
  const seenKeys = new Set(historyMsgs.map((m) => m.idempotencyKey).filter(Boolean));
  // 有 idempotencyKey 的消息：精确 key 匹配去重，不做内容去重。
  //   - key 已在历史里 → 真正的重复，过滤掉
  //   - key 不在历史里 → 一定是新消息，直接保留（相同内容也不过滤）
  // 无 idempotencyKey 的消息：降级按 role+内容 计数去重，避免双份展示。
  const contentCountMap = new Map<string, number>();
  historyMsgs.forEach((m) => {
    if (m.idempotencyKey) return; // 有 key 的历史消息已靠 seenKeys 处理，不计入内容表
    const k = `${m.role}\x00${getTextContent(m.content)}`;
    contentCountMap.set(k, (contentCountMap.get(k) ?? 0) + 1);
  });
  const dedupedNew = sessionNewMsgs.filter((m) => {
    if (m.idempotencyKey) {
      // 精确命中：key 在历史里 → 过滤；不在 → 新消息，保留
      return !seenKeys.has(m.idempotencyKey);
    }
    // 无 key 降级内容去重
    const contentKey = `${m.role}\x00${getTextContent(m.content)}`;
    const remaining = contentCountMap.get(contentKey) ?? 0;
    if (remaining > 0) {
      contentCountMap.set(contentKey, remaining - 1);
      return false;
    }
    return true;
  });
  const allMessages = [...historyMsgs, ...dedupedNew];

  // 向父组件同步当前可展示消息的 ID（与列表一致；「全选」不含空白/隐藏条）
  useEffect(() => {
    if (!onIdsChange) return;
    onIdsChange(
      allMessages
        .map((msg, idx) => ({ msg, idx }))
        .filter(({ msg }) => shouldShowBubble(msg))
        .map(({ msg, idx }) => getMsgId(msg, idx)),
    );
  }, [messages, historyMsgs, currentSessionKey, onIdsChange]);

  // 与 onIdsChange 同序、同过滤，用于分享勾选与 getMsgId 对齐
  useEffect(() => {
    onMessagesChange?.(allMessages.filter((msg) => shouldShowBubble(msg)));
  }, [messages, historyMsgs, currentSessionKey, onMessagesChange]);

  // 当前 session 的工具调用（按 startedAt 排序），用于流式消息的 ThinkingPanel
  const sessionToolCalls = toolCalls
    .filter((c) => getBaseKey(c.sessionKey) === getBaseKey(currentSessionKey ?? ''))
    .sort((a, b) => a.startedAt - b.startedAt);

  // 当前 session 的 lifecycle 事件，按 runId 分组
  const sessionLifecycleEvents = lifecycleEvents.filter(
    (e) => getBaseKey(e.sessionKey) === getBaseKey(currentSessionKey ?? ''),
  );
  const sessionLifecycleByRunId = new Map<string, LifecycleEvent[]>();
  for (const e of sessionLifecycleEvents) {
    const existing = sessionLifecycleByRunId.get(e.runId) ?? [];
    existing.push(e);
    sessionLifecycleByRunId.set(e.runId, existing);
  }

  // 用于检测「用户新发了一条消息」：跟踪 user 消息数量，增加时强制滚底
  const userMsgCount = allMessages.filter((m) => m.role === 'user').length;
  const prevUserMsgCountRef = useRef(0);

  // 首次进入 session 时无动画跳到底部；后续新消息/流式更新仅在「接近底部」时才跟随
  const lastContent = allMessages[allMessages.length - 1]?.content;
  useEffect(() => {
    const prevCount = prevUserMsgCountRef.current;
    prevUserMsgCountRef.current = userMsgCount;

    if (!initialScrolledRef.current) {
      // 还没做过首次滚动：有内容就立即跳底（instant），无论是历史还是实时消息
      if (allMessages.length > 0) {
        bottomRef.current?.scrollIntoView({ behavior: 'instant' });
        initialScrolledRef.current = true;
      }
    } else if (userMsgCount > prevCount) {
      // 用户刚发送了新消息：无论当前滚动位置，强制跳到底部并重置跟随状态
      // （发消息时 store 同时写入 user + assistant 占位，lastRole 此时已是 assistant，
      //   因此不能用 lastRole === 'user' 判断，改为对比用户消息计数）
      bottomRef.current?.scrollIntoView({ behavior: 'instant' });
      isNearBottomRef.current = true;
    } else if (isNearBottomRef.current) {
      // 用户在底部附近：跟随 assistant 流式更新平滑滚动
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    // 用户已向上滚动：不做任何滚动，保留当前阅读位置
  }, [allMessages.length, lastContent, userMsgCount]);

  if (!currentSessionKey) return null;

  return (
    <div className={`msg-list${shareMode ? ' msg-list--share' : ''}`} ref={listRef}>
      {loading && historyMsgs.length === 0 && sessionNewMsgs.length === 0 ? (
        <div className="msg-list__placeholder">加载历史消息中…</div>
      ) : allMessages.length === 0 ? (
        <div className="msg-list__placeholder">暂无消息</div>
      ) : (
        (() => {
          const lastAssistantIdx = allMessages.reduce(
            (acc, m, i) => (m.role === 'assistant' ? i : acc),
            -1,
          );

          // 把本次 session 所有 live tool calls 按 runId 分组。
          // 注意：tool calls 不在新 run 时清空，靠 runId 分组隔离各轮次。
          const sessionToolCallsByRunId = new Map<string, ToolCall[]>();
          for (const tc of sessionToolCalls) {
            if (!tc.runId) continue;
            const existing = sessionToolCallsByRunId.get(tc.runId) ?? [];
            existing.push(tc);
            sessionToolCallsByRunId.set(tc.runId, existing);
          }

          // 已被完成消息「认领」的 runId 集合：
          // finalized assistant 消息的 idempotencyKey === 其对应 run 的 server runId。
          // 凡在此集合内的 runId，其 tool calls / lifecycle 只归属于对应的完成消息，
          // 不会流给正在 streaming 的 placeholder，防止串消息。
          const claimedRunIds = new Set(
            allMessages
              .filter((m) => m.role === 'assistant' && m.done && m.idempotencyKey)
              .map((m) => m.idempotencyKey!),
          );

          // streaming placeholder 专用：未被任何完成消息认领的 tool calls / lifecycle events
          const unclaimedToolCalls = sessionToolCalls.filter((tc) => !claimedRunIds.has(tc.runId));
          const unclaimedLifecycleEvents = sessionLifecycleEvents.filter(
            (e) => !claimedRunIds.has(e.runId),
          );

          /** 查找某条 assistant 消息对应的工具调用 */
          const getToolCallsForMsg = (msg: ClawChatMessage, idx: number): ToolCall[] => {
            if (idx === lastAssistantIdx) {
              // 精确匹配：finalised 消息 idempotencyKey === server runId
              if (msg.idempotencyKey) {
                const byRunId = sessionToolCallsByRunId.get(msg.idempotencyKey) ?? [];
                if (byRunId.length > 0) return byRunId;
              }
              // streaming 占位阶段：只用「未被完成消息认领」的 tool calls，避免串前一轮
              return unclaimedToolCalls;
            }
            // 非最后一条：按 idempotencyKey 在 live 数据里查各自的 runId 分组
            if (msg.idempotencyKey) {
              const tc = sessionToolCallsByRunId.get(msg.idempotencyKey);
              if (tc?.length) return tc;
            }
            // 历史工具调用兜底
            if (msg.idempotencyKey) {
              const tc = historyToolCallsByKey.get(msg.idempotencyKey);
              if (tc?.length) return tc;
            }
            if (msg.timestamp) {
              const tc = historyToolCallsByKey.get(String(msg.timestamp));
              if (tc?.length) return tc;
            }
            return [];
          };

          /** 查找某条 assistant 消息对应的 lifecycle 事件 */
          const getLifecycleEventsForMsg = (msg: ClawChatMessage, idx: number): LifecycleEvent[] => {
            if (idx === lastAssistantIdx) {
              // 精确匹配
              if (msg.idempotencyKey) {
                const byRunId = sessionLifecycleByRunId.get(msg.idempotencyKey) ?? [];
                if (byRunId.length > 0) return byRunId;
              }
              // streaming 占位阶段：只用未被认领的 lifecycle events
              return unclaimedLifecycleEvents;
            }
            if (msg.idempotencyKey) {
              return sessionLifecycleByRunId.get(msg.idempotencyKey) ?? [];
            }
            return [];
          };

          // 孤立工具调用/thinking：无 assistant 文本消息时（如 aborted），独立展示
          const showOrphaned =
            (orphanedHistoryToolCalls.length > 0 || orphanedHistoryThinking.length > 0) &&
            lastAssistantIdx === -1 &&
            sessionToolCalls.length === 0;

          return (
            <>
              {allMessages.map((msg, idx) => {
                const id = getMsgId(msg, idx);
                if (!shouldShowBubble(msg)) return null;
                if (shareMode) {
                  const checked = selectedIds?.has(id) ?? false;
                  return (
                    <div
                      key={id}
                      className={`msg-select-row msg-select-row--${msg.role}`}
                      onClick={() => onToggle?.(id)}
                    >
                      <SelectRadio checked={checked} />
                      <Bubble
                        msg={msg}
                        toolCalls={getToolCallsForMsg(msg, idx)}
                        lifecycleEvents={getLifecycleEventsForMsg(msg, idx)}
                      />
                    </div>
                  );
                }
                return (
                  <Bubble
                    key={id}
                    msg={msg}
                    toolCalls={getToolCallsForMsg(msg, idx)}
                    lifecycleEvents={getLifecycleEventsForMsg(msg, idx)}
                  />
                );
              })}
              {showOrphaned && (
                <div className="msg-bubble msg-bubble--assistant">
                  <div className="msg-bubble__content msg-bubble__content--assistant">
                    <ThinkingPanel
                      toolCalls={orphanedHistoryToolCalls}
                      thinkingBlocks={orphanedHistoryThinking}
                      lifecycleEvents={[]}
                      isStreaming={false}
                    />
                  </div>
                </div>
              )}
            </>
          );
        })()
      )}
      <div ref={bottomRef} />
    </div>
  );
};

export default MessageList;
